import { defaultMultipleSelectProps } from "components/shared/select";
import {
    bidRefuelingServiceTypes,
    bidRoles,
    featureModuleTypesEnum,
    graphHopperRoadTypes,
    messengerTypes,
} from "helpers/enums";
import { minTime, requiredField } from "helpers/validators";
import { getAllUsersForSelectList } from "sources/admin";
import { list as getCustomPointTypes } from "sources/books/customPointTypes";
import { DriverEducationResources, EBillsForm, Exploitation, RefuelingPlanning, ReportSnapshotForm } from "./templates";

const config = {
    [featureModuleTypesEnum.DriverEducationResources]: {
        ModuleConfigComponent: DriverEducationResources,
        resources: {
            value: [],
            config: {
                name: {
                    label: "Название",
                    inputProps: { name: "name" },
                    required: true,
                    validators: [requiredField],
                },
                link: {
                    label: "Ссылка",
                    inputProps: { name: "link" },
                    required: true,
                    validators: [requiredField],
                },
            },
        },
    },
    [featureModuleTypesEnum.PlanningCarSchedule]: {
        averageSpeed: {
            label: "Средняя скорость машин (км/ч)",
            inputProps: { name: "averageSpeed", type: "number", placeholder: 80, min: 0 },
        },
        workHours: {
            label: "Кол-во часов для поездки в сутки",
            inputProps: { name: "workHours", type: "number", step: "1", placeholder: 8, min: 0, max: 24 },
        },
        maxLoadUnloadPeriod: {
            label: "Время загрузки/выгрузки (ч)",
            inputProps: { name: "maxLoadUnloadPeriod", type: "number", step: "1", placeholder: 4, min: 4, max: 24 },
        },
        emptyMileagePointStayPeriod: {
            label: "Время нахождения в точках в порожних заявках (чч:мм)",
            inputProps: { name: "emptyMileagePointStayPeriod" },
            type: "timespan",
            validators: [minTime("00:15")],
        },
        maxPlannedBidsCount: {
            label: "Макс. кол-во запланированных заявок",
            inputProps: { name: "maxPlannedBidsCount", type: "number", step: "1", placeholder: 1, min: 0, max: 30 },
        },
        dayStart: {
            label: "Начало дня",
            inputProps: { name: "dayStart", type: "time", skip: 600, placeholder: "08:00" },
        },
        matchingDistributionBidsRadius: {
            label: "Радиус подбора заказов (км)",
            inputProps: { name: "matchingDistributionBidsRadius", type: "number", placeholder: 40, min: 0 },
        },
        recommendedBreakTime: {
            // field
            label: "Рекомендуемое время перерыва",
            inputProps: { name: "recommendedBreakTime" },
            type: "timespan",
        },
        recommendedRestTime: {
            // field
            label: "Рекомендуемое время отдыха",
            inputProps: { name: "recommendedRestTime" },
            type: "timespan",
        },
        recommendedWorkTime: {
            // field
            label: "Рекомендуемое время работы",
            inputProps: { name: "recommendedWorkTime" },
            type: "timespan",
        },
        useLastCarLocationForEmptyMileageBid: {
            isCheckbox: true,
            label: "Использовать текущее местоположение машины при создании порожних заявок",
            inputProps: { name: "useLastCarLocationForEmptyMileageBid" },
        },
    },
    [featureModuleTypesEnum.RefuelingPlanning]: {
        ModuleConfigComponent: RefuelingPlanning,
        refuelingServiceType: {
            isSelect: true,
            label: "Сервис планирования заправок",
            inputProps: { name: "refuelingServiceType" },
            options: bidRefuelingServiceTypes,
            menuPortalTarget: document.body,
            unClearable: true,
        },
        addCarToRefuelingSyncByDefault: {
            isCheckbox: true,
            label: "Подключать машины к ТМ по умолчанию",
            inputProps: { name: "addCarToRefuelingSyncByDefault" },
        },
        addTrailerToRefuelingSyncByDefault: {
            isCheckbox: true,
            label: "Подключать прицепы к ТМ по умолчанию",
            inputProps: { name: "addTrailerToRefuelingSyncByDefault" },
        },
        isRefuelingPlanLimitedToOnce: {
            isCheckbox: true,
            label: "Планировать заправки по заявке один раз",
            inputProps: { name: "isRefuelingPlanLimitedToOnce" },
        },
        skipPlanningForPastBids: {
            isCheckbox: true,
            label: "Не планировать по заявкам с прошедшим плановым временем",
            inputProps: { name: "skipPlanningForPastBids" },
        },
        basePeriodStartDate: {
            label: "Дата начала базового периода",
            inputProps: { name: "basePeriodStartDate", type: "datetime" },
            inPortal: true,
        },
        multiGo: {
            accessKey: {
                label: "Ключ мультиго",
                inputProps: { name: "accessKey" },
            },
        },
        delkon: {
            accessKey: {
                label: "Ключ delkon",
                inputProps: { name: "accessKey" },
            },
            userName: {
                label: "Логин delkon",
                inputProps: { name: "userName" },
            },
            password: {
                label: "Пароль",
                inputProps: { name: "password", type: "password" },
            },
        },
        str: {
            userName: {
                label: "Логин СТР",
                inputProps: { name: "userName" },
            },
            password: {
                label: "Пароль",
                inputProps: { name: "password", type: "password" },
            },
        },
        multiGo2: {
            accessKey: {
                label: "Ключ мультиго 2",
                inputProps: { name: "accessKey" },
            },
        },
        solarFrps: {
            baseUrl: {
                label: "Точка доступа",
                inputProps: { name: "baseUrl", placeholder: "https://solar-frps.smprojects.ru" },
            },
            username: {
                label: "Логин",
                inputProps: { name: "username" },
            },
            password: {
                label: "Пароль",
                inputProps: { name: "password", type: "password" },
            },
            updatingPeriod: {
                label: "Период планирования АЗС в часах",
                inputProps: { name: "updatingPeriod", type: "number", step: "1", min: 1, max: 10 },
            },
            isFuelLimitEnabled: {
                isCheckbox: true,
                label: "Включить топливные лимиты",
                inputProps: { name: "isFuelLimitEnabled" },
            },
            filterByFuelCardType: {
                isCheckbox: true,
                label: "Фильтровать по топливным картам",
                inputProps: { name: "filterByFuelCardType" },
            },
            hideRefuelingPlanning: {
                isCheckbox: true,
                label: "Скрыть работу топливного модуля",
                inputProps: { name: "hideRefuelingPlanning" },
            },
            sendPlannedBidPath: {
                isCheckbox: true,
                label: "Передавать маршрут запланированной заявки",
                inputProps: { name: "sendPlannedBidPath" },
            },
        },
    },
    [featureModuleTypesEnum.AxisLoadMonitoring]: {
        carDefaultAxisLoad: {
            label: "Загрузка грузовика по умолчанию",
            inputProps: { name: "carDefaultAxisLoad" },
        },
        trailerDefaultAxisLoad: {
            label: "Загрузка прицепа по умолчанию",
            inputProps: { name: "trailerDefaultAxisLoad" },
        },
        cargoDefaultWeight: {
            label: "Вес груза по умолчанию",
            inputProps: { name: "cargoDefaultWeight" },
        },
    },
    [featureModuleTypesEnum.DriverControlPoints]: {
        checkVisitingTime: {
            label: "Время проверки посещения",
            required: true,
            validators: [requiredField],
            inputProps: { name: "checkVisitingTime", type: "time", skip: 600, placeholder: "08:00" },
        },
        checkLatenessTime: {
            label: "Время проверки успеваемости",
            required: true,
            validators: [requiredField],
            inputProps: { name: "checkLatenessTime", type: "time", skip: 600, placeholder: "16:00" },
        },
        planDailyMileage: {
            label: "Плановый ежедневный пробег",
            inputProps: { name: "planDailyMileage" },
        },
        customPointTypeIds: {
            isSelect: true,
            isMulti: true,
            url: getCustomPointTypes,
            label: "Типы маршрутных точек для уменьшения пробега на смену",
            inputProps: { name: "customPointTypeIds" },
            propName: "name",
        },
        maxMileageAfterCustomPoint: {
            label: "Значение уменьшения пробега",
            inputProps: { name: "maxMileageAfterCustomPoint" },
        },
    },
    [featureModuleTypesEnum.Exploitation]: {
        ModuleConfigComponent: Exploitation,
        apiKey: {
            label: "Ключ API",
            required: true,
            validators: [requiredField],
            inputProps: { name: "apiKey" },
        },
        approvingSettings: {
            considerCurrentLocation: {
                label: "Учитывать текущее местоположение машины",
                inputProps: { name: "considerCurrentLocation" },
                isCheckbox: true,
            },
        },
    },
    [featureModuleTypesEnum.Waybills]: {
        ModuleConfigComponent: EBillsForm,
        userIds: {
            isSelect: true,
            isMulti: true,
            url: ({ organizationId, ...params }) => getAllUsersForSelectList(params, { organizationId }),
            label: "Пользователи с доступом к подписыванию",
            inputProps: { name: "userIds" },
            propName: "fullNameOrEmail",
        },
        providerType: {
            label: "Операторы ЭПД",
            inputProps: { name: "providerType" },
        },
    },
    [featureModuleTypesEnum.TripScenarios]: {
        buttonName: {
            label: "Текст кнопки",
            required: true,
            validators: [requiredField],
            inputProps: { name: "buttonName" },
        },
    },
    [featureModuleTypesEnum.BidPointsAdjustment]: {
        geohashRadius: {
            label: "Радиус геохеша, в м",
            required: true,
            validators: [requiredField],
            inputProps: { name: "geohashRadius" },
        },
        searchRadius: {
            label: "Радиус поиска предполагаемой точки выгрузки, в км",
            required: true,
            validators: [requiredField],
            inputProps: { name: "searchRadius" },
        },
        startDelay: {
            label: "Задержка поиска точки выгрузки, в часах",
            required: true,
            validators: [requiredField],
            inputProps: { name: "startDelay" },
        },
    },
    [featureModuleTypesEnum.Messenger]: {
        type: {
            label: "Тип сервиса",
            isSelect: true,
            options: messengerTypes,
            menuPortalTarget: document.body,
            unClearable: true,
            inputProps: { name: "type" },
        },
    },
    [featureModuleTypesEnum.DocumentAssignment]: {
        withoutApprovalExternal: {
            isCheckbox: true,
            label: "Без согласования с 1С",
            inputProps: { name: "withoutApprovalExternal" },
        },
    },
    [featureModuleTypesEnum.TrackerLogsFiltering]: {
        useMapMatchingFilteringInManualMode: {
            isCheckbox: true,
            label: "Использовать фильтрацию координат в ручном режиме",
            inputProps: { name: "useMapMatchingFilteringInManualMode" },
        },
        useMapMatchingFilteringInAutoMode: {
            isCheckbox: true,
            label: "Использовать фильтрацию координат автоматически",
            inputProps: { name: "useMapMatchingFilteringInAutoMode" },
        },
        kNearestNeighbor: {
            label: "Количество ближайших соседей",
            inputProps: { name: "kNearestNeighbor", type: "number", min: 0 },
        },
        maxDistanceFromRoad: {
            label: "Максимальное расстояние от дороги",
            inputProps: { name: "maxDistanceFromRoadInMeters", type: "number", min: 1 },
        },
        minimumJumpDistance: {
            label: "Минимальный разрыв координат для начала эмуляции",
            inputProps: { name: "minimumJumpDistance", type: "number", min: 0 },
            tooltip: "При необходимости отключения эмуляции укажите значение 0",
        },
        useFilteringByOdometer: {
            isCheckbox: true,
            label: "Использовать фильтрацию по одометру",
            inputProps: { name: "useFilteringByOdometer" },
        },
        minimumJumpDistanceToCheckOdometer: {
            label: "Минимальный разрыв координат для проверки одометра",
            inputProps: { name: "minimumJumpDistanceToCheckOdometer", type: "number", min: 0 },
        },
        odometerCoefficient: {
            label: "Коэффициент одометра",
            inputProps: { name: "odometerCoefficient", type: "number", min: 0, max: 1, step: 0.1 },
        },
        useFilteringByMaxSpeed: {
            isCheckbox: true,
            label: "Использовать фильтрацию по макс. скорости",
            inputProps: { name: "useFilteringByMaxSpeed" },
        },
        maxSpeed: {
            label: "Максимальная скорость",
            inputProps: { name: "maxSpeed", type: "number", min: 0 },
        },
        roadTypes: {
            ...defaultMultipleSelectProps,
            label: "Тип дорог",
            isSelect: true,
            options: graphHopperRoadTypes,
            menuPortalTarget: document.body,
            unClearable: true,
            inputProps: { name: "roadTypes" },
        },
    },
    [featureModuleTypesEnum.RatingOfDriversAndLogists]: {
        bidRole: {
            label: "Роль в заявке, которая может оценивать водителя",
            isSelect: true,
            options: bidRoles,
            menuPortalTarget: document.body,
            unClearable: true,
            inputProps: { name: "bidRole" },
        },
    },
    [featureModuleTypesEnum.Forwarding]: {
        defaultTrailerTypeId: {
            label: "Тип прицепа по умолчанию",
            isSelect: true,
            dictionary: "trailerType",
            inputProps: { name: "defaultTrailerTypeId" },
        },
    },
    [featureModuleTypesEnum.ReportSnapshot]: {
        ModuleConfigComponent: ReportSnapshotForm,
        userId: {
            label: "Пользователь с возможностью фиксировать данные отчета",
            isSelect: true,
            url: ({ organizationId, ...params }) => getAllUsersForSelectList(params, { organizationId }),
            inputProps: { name: "userId" },
            propName: "fullNameOrEmail",
        },
    },
    [featureModuleTypesEnum.CrStart]: {
        hideTripSheets: {
            isCheckbox: true,
            label: "Не показывать путевые листы",
            inputProps: { name: "hideTripSheets" },
        },
    },
};

export { config };
